import { debounce } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import cx from "clsx";
import { cn } from "@/utils/cn";

export default function BaseSearch({
  searchKey = "search",
  placeholder = "Search Table...",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);

  const params = new URLSearchParams(location.search);
  const initialQuery = params.get(searchKey) || "";
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query) => {
        const newParams = new URLSearchParams(location.search);
        if (query) {
          newParams.set(searchKey, query);
          newParams.set("page", "1");
        } else {
          newParams.delete(searchKey);
        }
        navigate({ pathname: location.pathname, search: newParams.toString() });
      }, 500),
    [location.search, location.pathname, navigate, searchKey]
  );

  useEffect(() => {
    setSearchQuery(initialQuery);
    inputRef.current?.focus();
  }, [initialQuery, location.search, searchKey]);

  const onSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const onClear = () => {
    setSearchQuery("");
    params.delete(searchKey);
    navigate({ pathname: location.pathname, search: params.toString() });

    // Ensure cursor stays in input
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <div
      className={cn(
        "w-[300px] h-8 rounded-[7px] p-[1px] bg-input-border hover:bg-gradient-to-r from-[#2E6ABD] via-[#F84160] to-[#009900] relative transition-all duration-300",
        searchQuery
          ? "bg-gradient-to-r from-[#2E6ABD] via-[#F84160] to-[#009900]"
          : ""
      )}
    >
      <div className="w-full h-full flex items-center">
        <input
          ref={inputRef}
          value={searchQuery}
          onChange={onSearch}
          type="text"
          placeholder={placeholder}
          className="h-full text-sm font-light flex-1 focus:outline-none px-2 rounded-s-[6px] bg-white"
        />
        <button
          onClick={onClear}
          className={cx(
            "w-[74px] text-sm font-extralight h-[32px] mr-[-1px] rounded-e-[6px] bg-green-black-gradient text-white border-solid shadow-primary-inset"
          )}
        >
          Clear
        </button>
      </div>
    </div>
  );
}
